import React, { useEffect, useMemo, useState } from "react";
import { endpoints } from "@/lib/api";
import { Sparkles, Star, Users, X, ShieldCheck, ChevronDown, MessageCircleQuestion, Send } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import WinPhotos from "@/components/WinPhotos";
import FollowButton from "@/components/FollowButton";
import { CheckinDialog } from "@/components/WinsFromCircle";
import AuthorAvatar from "@/components/AuthorAvatar";
import PostActions, { InlinePostEditor } from "@/components/PostActions";
import { PostReactions, AskQuestion, JourneyQnA } from "@/components/CommunityInteractions";
import AuthenticityBadge from "@/components/AuthenticityBadge";
import CirclePromptOfDay from "@/components/CirclePromptOfDay";
import CollectiveStandards from "@/components/CollectiveStandards";
import N8Emblem from "@/components/N8Emblem";
import PageHero from "@/components/PageHero";
import { HERO } from "@/lib/heroImages";
import AskTheCollectiveComposer from "@/components/AskTheCollectiveComposer";
import SharedExperiences from "@/components/SharedExperiences";

// -------- post-type classification -----------------------------------------
// We do not add or invent categories. Types are derived from existing fields:
//   is_question    → QUESTION
//   is_win         → WIN
//   milestone_ref  → DISCOVERY (a personal milestone toward a chosen goal)
//   default        → SHARE
const TYPE_LABELS = {
  question:  { label: "Question",  color: "hsl(var(--moss))" },
  win:       { label: "Win",       color: "hsl(var(--terracotta))" },
  discovery: { label: "Discovery", color: "hsl(var(--clay))" },
  share:     { label: "Share",     color: "hsl(var(--muted-foreground))" },
};

function postType(p) {
  if (p.is_question) return "question";
  if (p.is_win) return "win";
  if (p.milestone_ref) return "discovery";
  return "share";
}

// -------- filter dropdown (age + goals) ------------------------------------
const CONTENT_FILTERS = [
  { slug: "all",       label: "All" },
  { slug: "question",  label: "Questions" },
  { slug: "win",       label: "Wins" },
  { slug: "discovery", label: "Discoveries" },
  { slug: "share",     label: "Shares" },
];

export default function Community() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(() =>
    initialTab === "circle" ? "circle" : "collective"
  );

  const [posts, setPosts] = useState([]);
  const [selfAuthor, setSelfAuthor] = useState("");
  const [circleMembers, setCircleMembers] = useState([]);
  const [authorFilter, setAuthorFilter] = useState(null);
  const [editingPostId, setEditingPostId] = useState(null);

  // Meta + profile defaults for the filter dropdown.
  const [meta, setMeta] = useState({ age_groups: [], goal_tags: [] });
  const [homeData, setHomeData] = useState(null);

  // Applied filters (what the feed is actually filtered by).
  const [ageGroup, setAgeGroup] = useState("all");
  const [selectedGoals, setSelectedGoals] = useState([]);
  // Draft filters (what the collapsible panel shows). Applied on Apply.
  const [draftAgeGroup, setDraftAgeGroup] = useState("all");
  const [draftGoals, setDraftGoals] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);

  // Content-type filter (chip row).
  const [typeFilter, setTypeFilter] = useState("all");

  // Primary-action dialogs.
  const [openShare, setOpenShare] = useState(false);
  const [openAsk, setOpenAsk] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [h, m] = await Promise.all([endpoints.home(), endpoints.meta()]);
        setHomeData(h.data);
        setMeta(m.data);
      } catch { /* silent */ }
    })();
  }, []);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (activeTab === "collective") next.delete("tab");
    else next.set("tab", activeTab);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadPosts = async () => {
    const { data } = await endpoints.posts("feed", { ageGroup, goals: selectedGoals });
    setPosts(data);
  };
  const loadProfile = async () => {
    const { data } = await endpoints.profile();
    setSelfAuthor(data.name || "");
  };
  const loadFollows = async () => {
    const { data } = await endpoints.follows();
    setCircleMembers(data.circle || []);
  };
  useEffect(() => { loadPosts(); loadProfile(); loadFollows(); }, []);

  // Re-fetch when the *applied* age/goal filters change.
  useEffect(() => {
    loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ageGroup, selectedGoals]);

  // Client-side apply of the content-type chip + author filter.
  const visiblePosts = useMemo(() => {
    let list = posts;
    if (authorFilter) list = list.filter((p) => p.author === authorFilter);
    if (typeFilter !== "all") list = list.filter((p) => postType(p) === typeFilter);
    return list;
  }, [posts, authorFilter, typeFilter]);

  // -------- primary handlers ----------------------------------------------
  const toggleDraftGoal = (slug) =>
    setDraftGoals((prev) => (prev.includes(slug) ? prev.filter((g) => g !== slug) : [...prev, slug]));
  const applyFilters = () => {
    setAgeGroup(draftAgeGroup);
    setSelectedGoals(draftGoals);
    setFilterOpen(false);
  };
  const clearFilters = () => {
    setDraftAgeGroup("all");
    setDraftGoals([]);
    setAgeGroup("all");
    setSelectedGoals([]);
  };
  const openFilterPanel = () => {
    setDraftAgeGroup(ageGroup);
    setDraftGoals(selectedGoals);
    setFilterOpen((v) => !v);
  };
  const activeFilterCount =
    (ageGroup !== "all" ? 1 : 0) + selectedGoals.length;

  const cheer = async (id) => {
    setPosts((prev) => prev.map((p) => {
      if (p.id !== id) return p;
      const next = !p.you_cheered;
      const delta = next ? 1 : -1;
      return { ...p, you_cheered: next, cheers_count: Math.max(0, (p.cheers_count || 0) + delta) };
    }));
    try {
      await endpoints.cheerPost(id);
    } catch (e) {
      loadPosts();
      toast.error(e?.response?.data?.detail || "Could not cheer.");
      return;
    }
    const p = posts.find((p) => p.id === id);
    if (p && !p.you_cheered) toast.success("Cheer sent.");
  };

  return (
    <div className="space-y-12" data-testid="community-page">
      <PageHero
        emblem=""
        eyebrow="Connection"
        title="The Collective"
        supporting="Growing with intention."
        body="Real experiences. Real journeys. Shared intentionally."
        image={HERO.community}
        testid="community-hero"
      />

      <div className="space-y-12 max-w-3xl mx-auto">

      {/* Primary actions — two clear intents. */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="community-primary-actions">
        <Button
          onClick={() => setOpenAsk(true)}
          data-testid="btn-ask-the-collective"
          className="rounded-full bg-[hsl(var(--moss))] hover:bg-[hsl(var(--moss))]/90 text-[hsl(var(--paper))] px-5 py-5 h-auto"
        >
          <MessageCircleQuestion size={16} className="mr-2" /> Ask the Collective
        </Button>
        <Button
          onClick={() => setOpenShare(true)}
          data-testid="btn-share"
          variant="outline"
          className="rounded-full border-[hsl(var(--clay))] text-[hsl(var(--clay))] hover:bg-[hsl(var(--clay))]/10 px-5 py-5 h-auto"
        >
          <Send size={14} className="mr-2" /> Share
        </Button>
      </div>

      {/* Culture statement + collapsible community guidelines. */}
      <CollectiveStandards />

      {/* Segmented navigation — two tabs only. */}
      <div
        role="tablist"
        aria-label="Connection sections"
        className="flex items-center gap-6 border-b hairline pb-4"
        data-testid="community-tabs"
      >
        <TabLink
          active={activeTab === "collective"}
          onClick={() => { setActiveTab("collective"); setAuthorFilter(null); }}
          testid="community-tab-collective"
          label="The Collective"
        />
        <TabLink
          active={activeTab === "circle"}
          onClick={() => setActiveTab("circle")}
          testid="community-tab-circle"
          label="Your Circle"
          count={circleMembers.length || null}
        />
      </div>

      {/* Explore / content-type chip row + collapsible filter. */}
      {/* Primary Community experience — The Collective feed. Static
          N8ONE emblem sits above the explore section to mark the shared
          space as an N8ONE experience rather than branding any
          individual post. */}
      <section className="space-y-4" data-testid="community-explore">
        <div className="flex items-center gap-3" data-testid="community-emblem-wrap">
          <N8Emblem
            mode="simplified"
            size={32}
            ariaLabel="N8ONE emblem"
            testid="community-emblem"
          />
          <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
            The Collective
          </div>
        </div>
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))]">
            Explore
          </div>
          <button
            type="button"
            onClick={openFilterPanel}
            aria-expanded={filterOpen}
            aria-controls="collective-filter-panel"
            data-testid="btn-filter-collective"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] tab-link"
          >
            Filter the Collective
            {activeFilterCount > 0 && (
              <span className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-[hsl(var(--clay))] text-[hsl(var(--paper))] text-[9px] tabular-nums" data-testid="filter-active-count">
                {activeFilterCount}
              </span>
            )}
            <ChevronDown size={12} className={`transition-transform duration-300 ${filterOpen ? "rotate-180" : ""}`} />
          </button>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="community-type-chips" role="tablist" aria-label="Post types">
          {CONTENT_FILTERS.map((c) => (
            <button
              key={c.slug}
              type="button"
              role="tab"
              aria-selected={typeFilter === c.slug}
              onClick={() => setTypeFilter(c.slug)}
              data-testid={`type-chip-${c.slug}`}
              className={`px-3.5 py-1.5 rounded-full text-[12px] tab-link ${
                typeFilter === c.slug
                  ? "bg-[hsl(var(--fg))] text-[hsl(var(--paper))]"
                  : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Collapsible filter panel — collapsed by default, kept subtle. */}
        <div
          id="collective-filter-panel"
          role="region"
          aria-hidden={!filterOpen}
          data-testid="community-filter-panel"
          style={{
            maxHeight: filterOpen ? "800px" : "0px",
            opacity: filterOpen ? 1 : 0,
            transition: "max-height 400ms ease, opacity 300ms ease",
          }}
          className="overflow-hidden"
        >
          <div className="pt-4 pl-4 border-l-2 border-[hsl(var(--clay))]/25 space-y-5 max-w-2xl">
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] mb-2">Age group</div>
              <div className="flex flex-wrap gap-2">
                <FilterPill active={draftAgeGroup === "all"} onClick={() => setDraftAgeGroup("all")} testid="filter-age-all">All ages</FilterPill>
                {meta.age_groups.map((a) => (
                  <FilterPill key={a} active={draftAgeGroup === a} onClick={() => setDraftAgeGroup(a)} testid={`filter-age-${a}`}>
                    {a}
                  </FilterPill>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] mb-2">Goals</div>
              <div className="flex flex-wrap gap-2">
                {meta.goal_tags.map((g) => (
                  <FilterPill
                    key={g.slug}
                    active={draftGoals.includes(g.slug)}
                    onClick={() => toggleDraftGoal(g.slug)}
                    testid={`filter-goal-${g.slug}`}
                  >
                    {g.name}
                  </FilterPill>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <Button
                onClick={applyFilters}
                data-testid="btn-apply-filters"
                size="sm"
                className="rounded-full bg-[hsl(var(--clay))] hover:bg-[hsl(var(--clay))]/90 text-[hsl(var(--paper))]"
              >
                Apply Filters
              </Button>
              <button
                type="button"
                onClick={clearFilters}
                data-testid="btn-clear-filters"
                className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] tab-link"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Feed */}
      {activeTab === "collective" && (
        <section
          className="space-y-8"
          data-testid="community-collective-panel"
          aria-labelledby="tab-collective"
        >
          <CirclePromptOfDay />
          <div className="mt-4">
            <Link_top nav={nav} />
          </div>
          {authorFilter && (
            <div
              className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-full bg-[hsl(var(--moss))]/12 text-sm"
              data-testid="author-filter-banner"
            >
              <div className="inline-flex items-center gap-2">
                <Users size={13} className="text-[hsl(var(--moss))]" />
                <span>Showing only <span className="font-medium">{authorFilter}</span></span>
              </div>
              <button
                onClick={() => setAuthorFilter(null)}
                data-testid="clear-author-filter"
                className="inline-flex items-center gap-1 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
              >
                <X size={12} /> clear
              </button>
            </div>
          )}
          <Feed
            posts={visiblePosts}
            selfAuthor={selfAuthor}
            editingPostId={editingPostId}
            setEditingPostId={setEditingPostId}
            onCheer={cheer}
            onChanged={() => { loadPosts(); loadFollows(); }}
            setPosts={setPosts}
            emptyMessage={
              typeFilter !== "all"
                ? "Nothing to show for this filter yet."
                : "It's quiet here. When someone shares, their words will land."
            }
          />
        </section>
      )}

      {activeTab === "circle" && (
        <section
          className="space-y-8"
          data-testid="community-circle-panel"
          aria-labelledby="tab-circle"
        >
          <YourCircle
            members={circleMembers}
            activeAuthor={authorFilter}
            onSelect={(name) => setAuthorFilter((prev) => (prev === name ? null : name))}
            onClear={() => setAuthorFilter(null)}
          />
          {circleMembers.length > 0 && (
            <Feed
              posts={visiblePosts.filter((p) => p.is_followed)}
              selfAuthor={selfAuthor}
              editingPostId={editingPostId}
              setEditingPostId={setEditingPostId}
              onCheer={cheer}
              onChanged={() => { loadPosts(); loadFollows(); }}
              setPosts={setPosts}
              emptyMessage={
                authorFilter
                  ? `${authorFilter} hasn't shared anything yet.`
                  : "Your Circle is quiet today. Their next reflection will land here."
              }
              testid="circle-posts"
            />
          )}
        </section>
      )}

      {/* Controlled dialogs — no visible internal trigger. */}
      {homeData && (
        <>
          <CheckinDialog
            mode="share"
            hideTrigger
            open={openShare}
            onOpenChange={setOpenShare}
            defaultGoals={homeData.profile.longevity_goals || []}
            defaultAgeGroup={homeData.profile.age_group || "30-40"}
            goalTags={meta.goal_tags}
            ageGroups={meta.age_groups}
            onPosted={async () => { setOpenShare(false); await loadPosts(); toast.success("Shared."); }}
          />
          <AskTheCollectiveComposer
            open={openAsk}
            onOpenChange={setOpenAsk}
            createPost={(payload) => endpoints.createPost(payload)}
            onPosted={async () => { await loadPosts(); }}
          />
        </>
      )}
      </div>
    </div>
  );
}

// -------- helpers ----------------------------------------------------------

function Link_top({ nav }) {
  return (
    <div className="text-right">
      <button
        onClick={() => nav("/moderation")}
        data-testid="community-moderation-link"
        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--terracotta))] tab-link"
      >
        <ShieldCheck size={11} /> Authenticity queue
      </button>
    </div>
  );
}

function TabLink({ active, onClick, testid, label, count }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testid}
      className={`relative pb-3 text-[15px] tab-link ${
        active ? "text-[hsl(var(--fg))]" : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
      }`}
    >
      <span className="font-medium">{label}</span>
      {typeof count === "number" && count > 0 && (
        <span className="ml-2 text-[10px] tabular-nums text-[hsl(var(--muted-foreground))]">{count}</span>
      )}
      {active && (
        <span
          className="absolute left-0 right-0 -bottom-[1px] h-[2px] bg-[hsl(var(--clay))] rounded-full"
          aria-hidden
        />
      )}
    </button>
  );
}

function FilterPill({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`px-3 py-1.5 rounded-full text-[12px] border tab-link ${
        active
          ? "bg-[hsl(var(--clay))] text-[hsl(var(--paper))] border-transparent"
          : "hairline text-[hsl(var(--fg))]/80 hover:bg-[hsl(var(--sand))]/40"
      }`}
    >
      {children}
    </button>
  );
}

// -------- editorial feed + post card --------------------------------------

function Feed({ posts, selfAuthor, editingPostId, setEditingPostId, onCheer, onChanged, setPosts, emptyMessage, testid }) {
  if (posts.length === 0) {
    return (
      <div className="py-8 text-sm text-[hsl(var(--muted-foreground))] italic" data-testid="posts-empty">
        {emptyMessage}
      </div>
    );
  }
  return (
    <div className="divide-y divide-[hsl(var(--line))]/60" data-testid={testid || "feed"}>
      {posts.map((p) => (
        <PostCard
          key={p.id}
          post={p}
          selfAuthor={selfAuthor}
          editing={editingPostId === p.id}
          setEditing={(on) => setEditingPostId(on ? p.id : null)}
          onCheer={onCheer}
          onChanged={onChanged}
          setPosts={setPosts}
        />
      ))}
    </div>
  );
}

function PostCard({ post: p, selfAuthor, editing, setEditing, onCheer, onChanged, setPosts }) {
  const type = postType(p);
  const t = TYPE_LABELS[type];
  const hasPhoto = !!(p.photo_path || (p.photo_before_path && p.photo_after_path));
  return (
    <article
      className="py-8 first:pt-2 last:pb-2"
      data-testid={`post-${p.id}`}
    >
      {/* Type + timestamp eyebrow. */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex items-center gap-3">
          <span
            className="text-[10px] uppercase tracking-[0.28em]"
            style={{ color: t.color }}
            data-testid={`post-type-${p.id}`}
          >
            {t.label}
          </span>
          <span className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
            {new Date(p.created_at).toLocaleDateString()}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <FollowButton
            author={p.author}
            following={!!p.is_followed}
            onChange={onChanged}
            selfAuthor={selfAuthor}
            testid={`post-follow-${p.id}`}
          />
          <PostActions
            post={p}
            isOwner={selfAuthor && p.author === selfAuthor}
            editing={editing}
            setEditing={setEditing}
            onChanged={() => { setEditing(false); onChanged(); }}
          />
        </div>
      </div>

      {/* Author. */}
      <div className="mt-4 flex items-center gap-3">
        <AuthorAvatar
          name={p.author}
          src={p.author_avatar_url}
          size="md"
          testid={`post-avatar-${p.id}`}
        />
        <div className="min-w-0">
          <div className="font-medium text-[15px] flex items-center gap-2">
            {p.author}
            {p.is_followed && (
              <span className="text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded-full bg-[hsl(var(--moss))]/12 text-[hsl(var(--moss))]" data-testid={`post-following-badge-${p.id}`}>
                In your circle
              </span>
            )}
          </div>
          <div className="text-[11px] tracking-[0.02em] text-[hsl(var(--muted-foreground))]" data-testid={`post-handle-${p.id}`}>
            {p.author_username ? (
              <span className="text-[hsl(var(--clay))]">@{p.author_username}</span>
            ) : (
              <span className="italic opacity-70">no handle yet</span>
            )}
          </div>
        </div>
      </div>

      {/* Content. */}
      {editing ? (
        <div className="mt-5">
          <InlinePostEditor
            post={p}
            onDone={() => { setEditing(false); onChanged(); }}
            onCancel={() => setEditing(false)}
          />
        </div>
      ) : (
        <div className="mt-5 space-y-2">
          <p
            className={`whitespace-pre-wrap leading-relaxed ${
              type === "question"
                ? "font-display italic text-xl md:text-2xl text-[hsl(var(--fg))]/90"
                : "text-[15px] text-[hsl(var(--fg))]/90"
            }`}
          >
            {p.content}
          </p>
          {p.edited_at && (
            <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]" data-testid={`post-edited-${p.id}`}>
              edited
            </div>
          )}
        </div>
      )}

      {/* Milestone (discovery) pill. */}
      {p.milestone_ref && (
        <div
          data-testid={`post-milestone-${p.id}`}
          className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[hsl(var(--terracotta))]/12 text-xs"
        >
          <Sparkles size={12} className="text-[hsl(var(--terracotta))]" />
          <span className="uppercase tracking-[0.18em] text-[10px] text-[hsl(var(--terracotta))]">
            {p.milestone_ref.category_name}
          </span>
          <span className="opacity-60">·</span>
          <span>{p.milestone_ref.target}</span>
          {p.milestone_ref.target_date && (
            <>
              <span className="opacity-60">·</span>
              <span className="text-[hsl(var(--muted-foreground))]">by {p.milestone_ref.target_date}</span>
            </>
          )}
        </div>
      )}

      {/* Photos — rendered ONLY when photos actually exist. */}
      {hasPhoto && (
        <div className="mt-5">
          <WinPhotos post={p} size="lg" />
          {(p.photo_before_path && p.photo_after_path) && (
            <div className="mt-2" data-testid={`post-authenticity-${p.id}`}>
              <AuthenticityBadge authenticity={p.authenticity} />
            </div>
          )}
        </div>
      )}

      {/* Interactions. */}
      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <PostReactions
          post={p}
          onChanged={(updated) =>
            setPosts((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)))
          }
        />
        <AskQuestion post={p} isCreator={p.author === selfAuthor} />
        {p.milestone_ref && (
          <button
            onClick={() => onCheer(p.id)}
            data-testid={`cheer-${p.id}`}
            aria-pressed={!!p.you_cheered}
            className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1 rounded-full border hairline text-xs tab-link ${
              p.you_cheered
                ? "bg-[hsl(var(--terracotta))]/20 text-[hsl(var(--terracotta))] border-[hsl(var(--terracotta))]/40"
                : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
            }`}
            title={p.you_cheered ? "You are cheering for them" : "Send a cheer"}
          >
            <Star size={12} strokeWidth={2} fill={p.you_cheered ? "currentColor" : "none"} />
            {p.you_cheered ? "Cheering" : "Cheer them on"}
            {p.cheers_count > 0 && (
              <span className="ml-1 text-[10px] tabular-nums opacity-80" data-testid={`cheer-count-${p.id}`}>
                {p.cheers_count}
              </span>
            )}
          </button>
        )}
      </div>
      <JourneyQnA post={p} />
      {/* SHARED EXPERIENCES — one-level responses beneath an Ask the
          Collective question. Not shown on share / win / discovery posts. */}
      {type === "question" && (
        <SharedExperiences post={p} selfUserId={p.author_user_id === selfAuthor ? p.author_user_id : undefined} />
      )}
    </article>
  );
}

// -------- your circle ------------------------------------------------------

function YourCircle({ members, activeAuthor, onSelect, onClear }) {
  if (!members || members.length === 0) {
    return (
      <div className="py-6" data-testid="your-circle-empty">
        <div className="font-display text-2xl italic leading-tight">Your circle is quiet.</div>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-md">
          Follow someone whose practice you admire and they will gather here.
        </p>
      </div>
    );
  }
  return (
    <div data-testid="your-circle">
      <div className="flex items-baseline justify-between mb-4">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))]">Your circle</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
          {members.length} {members.length === 1 ? "person" : "people"}
        </div>
      </div>
      <div className="flex gap-3 flex-wrap" data-testid="your-circle-wrap">
        {members.map((m) => {
          const active = m.author === activeAuthor;
          return (
            <button
              key={m.author}
              type="button"
              onClick={() => onSelect(m.author)}
              data-testid={`circle-member-${m.author}`}
              aria-pressed={active}
              className={`group inline-flex items-center gap-3 pl-1.5 pr-4 py-1.5 rounded-full border tab-link ${
                active
                  ? "bg-[hsl(var(--moss))] text-[hsl(var(--paper))] border-transparent"
                  : "hairline hover:bg-[hsl(var(--sand))]"
              }`}
            >
              <AuthorAvatar
                name={m.author}
                src={m.avatar_url}
                size="sm"
                ring={active ? "moss" : null}
              />
              <span className="text-left leading-tight">
                <span className="block text-sm font-medium">{m.author}</span>
                <span className={`block text-[10px] uppercase tracking-[0.18em] ${active ? "opacity-80" : "text-[hsl(var(--muted-foreground))]"}`}>
                  {m.post_count > 0 ? `${m.post_count} ${m.post_count === 1 ? "post" : "posts"}` : "quiet"}
                </span>
              </span>
            </button>
          );
        })}
        {activeAuthor && (
          <button
            onClick={onClear}
            data-testid="your-circle-show-all"
            className="text-xs text-[hsl(var(--muted-foreground))] underline underline-offset-4 hover:text-[hsl(var(--clay))] ml-1 self-center"
          >
            show everyone
          </button>
        )}
      </div>
    </div>
  );
}
